import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!password || password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (!token) { toast.error("Invalid reset link"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset successfully!");
      navigate("/login");
    } catch(e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <Card className="w-full max-w-md p-8 border-border shadow-organic">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#D0F5F2] flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={24} className="text-[#2A9D8F]" />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Set new password</h1>
            <p className="text-sm text-muted-foreground mt-2">Choose a strong password for your account.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">New password</Label>
              <div className="relative mt-1.5">
                <Input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters" className="h-11 pr-10" />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Confirm password</Label>
              <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Repeat your password" className="mt-1.5 h-11"
                onKeyDown={e => e.key === "Enter" && handleSubmit()} />
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="w-full h-11 bg-[#2A9D8F] hover:bg-[#23887A]">
              {submitting ? "Resetting…" : "Reset password"}
            </Button>
            <div className="text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Back to sign in</Link>
            </div>
          </div>
        </Card>
      </div>
    </PublicLayout>
  );
}
